import clsx from "clsx";
import {
  addHours,
  compareDesc,
  eachDayOfInterval,
  format,
  formatDate,
  isWeekend,
} from "date-fns";
import {
  Button,
  CheckBox,
  DateBox,
  LoadPanel,
  Popup,
  RadioGroup,
  SelectBox,
  TagBox,
  TextArea,
} from "devextreme-react";
import { NumberBox, type NumberBoxTypes } from "devextreme-react/number-box";
import { ToolbarItem } from "devextreme-react/popup";
import { confirm } from "devextreme/ui/dialog";
import notify from "../../lib/notify";
import { produce } from "immer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/auth";
import axios from "../../lib/axios";
import { useThemeContext } from "../../theme/theme";
import {
  TypeOfDayOff,
  TypeOfWork,
  WorkPlanEmployeeWithDates,
} from "../../types";
import { useScreenSize } from "../../utils/media-query";
import NumberBoxWrapper from "./NumberBoxComponent";

const daysMapping: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const monthMapping: Record<string, number> = {
  january: 0,
  february: 1,
  march: 2,
  april: 3,
  may: 4,
  june: 5,
  july: 6,
  august: 7,
  september: 8,
  october: 9,
  november: 10,
  december: 11,
};

export type EmployeesToSelect = {
  companyId: number;
  employeeId: number;
  name: string;
};

interface WorkPlanEditorProps {
  employeesWithDates: WorkPlanEmployeeWithDates[];
  employeesToSelect: EmployeesToSelect[];
  onUpdate: () => void;
  onClose: () => void;
  onDelete?: (entry: {
    companyId: number;
    employeeId: number;
    date: Date;
  }) => Promise<void> | void;
}

type Entry = {
  from: Date;
  to: Date;
  typeOfWork: number;
  departmentId: number;
  projectId: number;
  note: string;
  showDetails: boolean;
};

export default function WorkPlanEditor({
  employeesWithDates,
  employeesToSelect,
  onUpdate,
  onClose,
  onDelete,
}: WorkPlanEditorProps) {
  const { t } = useTranslation();
  const { theme } = useThemeContext();
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;

  const dayTypes = useMemo<{ id: number; name: string }[]>(
    () => [
      {
        id: 1,
        name: t("workPlanEditor.typesOfDay.workingDay"),
      },
      {
        id: 2,
        name: t("workPlanEditor.typesOfDay.dayOff"),
      },
      {
        id: 3,
        name: t("workPlanEditor.typesOfDay.holiday"),
      },
    ],
    [t]
  );

  const [loading, setLoading] = useState(false);
  const [typesOfWork, setTypesOfWork] = useState<TypeOfWork[]>([]);
  const [typesOfDayOff, setTypesOfDayOff] = useState<TypeOfDayOff[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [from, setFrom] = useState<Date>(new Date());
  const [to, setTo] = useState<Date>(new Date());
  const [typeOfDay, setTypeOfDay] = useState(1);
  const [kindOfDayOff, setKindOfDayOff] = useState<number | null>(null);
  const [skipDaysOffAndHolidays, setSkipDaysOffAndHolidays] = useState(true);
  const [entrances, setEntrances] = useState<Entry[]>([]);
  const [isDayOffOrHoliday, setIsDayOffOrHoliday] = useState(false);
  const [disableEmployeesTagBox, setDisableEmployeesTagBox] = useState(false);
  const [hideDateInputs, setHideDateInputs] = useState(false);
  const { user } = useAuth();
  const [selectedDay, setSelectedDay] = useState<string>("");
  const [monthSelect, setMonthSelect] = useState<string>("");
  const [selectedDayInWeek, setSelectedDayInWeek] = useState<string>("");
  const [triggerType, setTriggerType] = useState("");
  const [triggerTypeDaily, setTriggerTypeDaily] = useState("");
  const [triggerTypeMonthly, setTriggerTypeMonthly] = useState("");
  const [triggerTypeYearly, setTriggerTypeYearly] = useState("");
  const [numericValue, setNumericValue] = useState<number>(1);
  const [numericValue1, setNumericValue1] = useState<number>(1);
  const [numericValue2, setNumericValue2] = useState<number>(1);
  const [numericValue3, setNumericValue3] = useState<number>(1);
  const [numericValue4, setNumericValue4] = useState<number>(1);
  const [numericValue5, setNumericValue5] = useState<number>(1);
  const [popupScheduleVisible, setPopupScheduleVisible] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [onlyWeekendDays, setOnlyWeekendDays] = useState(false);
  const [planExists, setPlanExists] = useState(false);

  const isSingleEmployeeSingleDate =
    employeesWithDates.length === 1 && employeesWithDates[0].dates.length === 1;

  const handleNumberBoxChange = useCallback(
    (setState: React.Dispatch<React.SetStateAction<number>>) =>
      (e: NumberBoxTypes.ValueChangedEvent) => {
        setState(e.value ?? 0);
      },
    []
  );

  const daysOfWeek = useMemo(() => [
    { id: 0, name: t("schedule.monday") },
    { id: 1, name: t("schedule.tuesday") },
    { id: 2, name: t("schedule.wednesday") },
    { id: 3, name: t("schedule.thursday") },
    { id: 4, name: t("schedule.friday") },
    { id: 5, name: t("schedule.saturday") },
    { id: 6, name: t("schedule.sunday") },
  ], [t]);

  const daysOptions = useMemo(() => [
    { value: "monday", label: t("schedule.monday") },
    { value: "tuesday", label: t("schedule.tuesday") },
    { value: "wednesday", label: t("schedule.wednesday") },
    { value: "thursday", label: t("schedule.thursday") },
    { value: "friday", label: t("schedule.friday") },
    { value: "saturday", label: t("schedule.saturday") },
    { value: "sunday", label: t("schedule.sunday") },
    { value: "weekday", label: t("schedule.weekday") },
    { value: "day", label: t("schedule.day") },
    { value: "holiday", label: t("schedule.holiday") },
  ], [t]);

  const monthOptions = useMemo(() => [
    { value: "january", label: t("schedule.january") },
    { value: "february", label: t("schedule.february") },
    { value: "march", label: t("schedule.march") },
    { value: "april", label: t("schedule.april") },
    { value: "may", label: t("schedule.may") },
    { value: "june", label: t("schedule.june") },
    { value: "july", label: t("schedule.july") },
    { value: "august", label: t("schedule.august") },
    { value: "september", label: t("schedule.september") },
    { value: "october", label: t("schedule.october") },
    { value: "november", label: t("schedule.november") },
    { value: "december", label: t("schedule.december") },
  ], [t]);

  const daysOptionsInWeek = useMemo(() => [
    { value: "1", label: t("1.") },
    { value: "2", label: t("2.") },
    { value: "3", label: t("3.") },
    { value: "4", label: t("4.") },
    { value: "last", label: t("schedule.last") },
  ], [t]);

  const triggerOptions = useMemo(() => [
    { key: "schedule.daily", label: t("schedule.daily") },
    { key: "schedule.weekly", label: t("schedule.weekly") },
    { key: "schedule.monthly", label: t("schedule.monthly") },
    { key: "schedule.yearly", label: t("schedule.yearly") },
  ], [t]);

  const triggerOptionsMonthly = useMemo(() => [
    { key: "schedule.monthDay", label: t("schedule.monthDay") },
    { key: "schedule.inTheNextDay", label: t("schedule.inTheNextDay") },
  ], [t]);

  const triggerOptionsDaily = useMemo(() => [
    { key: "schedule.what", label: t("schedule.what") },
    { key: "schedule.generalDay", label: t("schedule.generalDay") },
  ], [t]);

  const triggerOptionsYearly = useMemo(() => [
    { key: "schedule.data", label: t("schedule.data") },
    { key: "schedule.day", label: t("schedule.day") },
  ], [t]);

  const isHolidayOrDayOff = (date) => {
    // const holidays = ["2025-01-01", "2025-12-25"];
    const holidays = [] as string[];

    const formattedDate = format(date, "yyyy-MM-dd");
    return isWeekend(date) || holidays.includes(formattedDate);
  };

  function getDatesInRange(startDateTime: Date, endDateTime: Date): Date[] {
    const dates: Date[] = [];
    const currentDate = new Date(startDateTime);

    while (currentDate <= endDateTime) {
      dates.push(new Date(currentDate));

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  }

  function getDates(startDateTime: Date, endDateTime: Date): Date[] {
    let datesInRange: Date[] = [];
    const currentDate = new Date(startDateTime);

    if (triggerType === "schedule.daily") {
      if (triggerTypeDaily === "schedule.what") {
        while (currentDate <= endDateTime) {
          datesInRange.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + numericValue!);
        }
      } else if (triggerTypeDaily === "schedule.generalDay") {
        datesInRange = getDatesInRange(startDateTime, endDateTime);
      }
    } else if (triggerType === "schedule.weekly") {
      const selectedDaysMap: Map<number, boolean> = new Map(
        selectedDays.map((day: number) => [day, true])
      );

      let currentWeekStart = new Date(startDateTime);
      let startDay = currentWeekStart.getDay();
      let diff = (startDay === 0 ? -6 : 1) - startDay;
      currentWeekStart.setDate(currentWeekStart.getDate() + diff);

      if (currentWeekStart < startDateTime) {
        currentWeekStart.setDate(currentWeekStart.getDate() + 7);
      }

      while (currentWeekStart <= endDateTime) {
        for (let i = 0; i < 7; i++) {
          const dateToAdd = new Date(currentWeekStart);
          dateToAdd.setDate(currentWeekStart.getDate() + i);
          if (selectedDaysMap.has((dateToAdd.getDay() + 6) % 7)) {
            if (dateToAdd >= startDateTime && dateToAdd <= endDateTime) {
              datesInRange.push(new Date(dateToAdd));
            }
          }
        }

        currentWeekStart.setDate(
          currentWeekStart.getDate() + 7 * numericValue5
        );
      }
    } else if (triggerType === "schedule.monthly") {
      if (triggerTypeMonthly === "schedule.monthDay") {
        let tempDate = new Date(startDateTime);

        while (true) {
          let newDate = new Date(
            tempDate.getFullYear(),
            tempDate.getMonth(),
            numericValue1
          );

          if (newDate > endDateTime) break;

          if (newDate >= startDateTime) {
            datesInRange.push(newDate);
          }

          tempDate.setMonth(tempDate.getMonth() + numericValue2);
        }
      } else if (triggerTypeMonthly === "schedule.inTheNextDay") {
        let tempDate = new Date(startDateTime);

        const selectedDayNumber = daysMapping[selectedDay];

        while (tempDate <= endDateTime) {
          let firstDayOfMonth = new Date(
            tempDate.getFullYear(),
            tempDate.getMonth(),
            1
          );
          let currentDay = new Date(firstDayOfMonth);
          let weekCount = 0;

          while (currentDay.getMonth() === firstDayOfMonth.getMonth()) {
            if (currentDay.getDay() === selectedDayNumber) {
              weekCount++;

              if (
                weekCount.toString() === selectedDayInWeek ||
                (selectedDayInWeek === "last" &&
                  currentDay.getMonth() === tempDate.getMonth())
              ) {
                if (currentDay >= startDateTime && currentDay <= endDateTime) {
                  datesInRange.push(new Date(currentDay));
                }
                break;
              }
            }
            currentDay.setDate(currentDay.getDate() + 1);
          }

          tempDate.setMonth(tempDate.getMonth() + numericValue3);
        }
      }
    } else if (triggerType === "schedule.yearly") {
      if (triggerTypeYearly === "schedule.data") {
        let tempYear = startDateTime.getFullYear();
        const endYear = endDateTime.getFullYear();

        const monthIndex = monthMapping[monthSelect];

        if (monthIndex !== undefined) {
          while (tempYear <= endYear) {
            let targetDate = new Date(tempYear, monthIndex, numericValue5);

            if (targetDate >= startDateTime && targetDate <= endDateTime) {
              datesInRange.push(targetDate);
            }

            tempYear += numericValue4;
          }

          let lastYearDate = new Date(endYear, monthIndex, numericValue5);
          if (lastYearDate >= startDateTime && lastYearDate <= endDateTime) {
            datesInRange.push(lastYearDate);
          }
        }
      } else if (triggerTypeYearly === "schedule.day") {
        let tempYear = startDateTime.getFullYear();
        const endYear = endDateTime.getFullYear();

        const monthIndex = monthMapping[monthSelect];
        const selectedDayNumber = daysMapping[selectedDay];

        if (monthIndex !== undefined && selectedDayNumber !== undefined) {
          while (tempYear <= endYear) {
            let firstDayOfMonth = new Date(tempYear, monthIndex, 1);
            let weekCount = 0;
            let foundDate: Date | null = null;

            while (firstDayOfMonth.getMonth() === monthIndex) {
              if (firstDayOfMonth.getDay() === selectedDayNumber) {
                weekCount++;
                if (
                  weekCount.toString() === selectedDayInWeek ||
                  (selectedDayInWeek === "last" &&
                    firstDayOfMonth.getMonth() !== monthIndex)
                ) {
                  foundDate = new Date(firstDayOfMonth);
                  break;
                }
              }
              firstDayOfMonth.setDate(firstDayOfMonth.getDate() + 1);
            }

            if (
              foundDate &&
              foundDate >= startDateTime &&
              foundDate <= endDateTime
            ) {
              datesInRange.push(foundDate);
            }

            tempYear += numericValue4;
          }
        }
      }
    } else {
      datesInRange = getDatesInRange(startDateTime, endDateTime);
    }

    return datesInRange;
  }

  const handleMoreOptions = () => {
    setPopupScheduleVisible((prevState) => !prevState);

    if (popupScheduleVisible === false) {
      setTriggerType("");
    }
  };

  const handleTriggerTypeChange = (e) => {
    setTriggerType(e.value);
  };

  const handleTriggerTypeChangeDaily = (e) => {
    setTriggerTypeDaily(e.value);
  };

  const handleTriggerTypeChangeMonthly = (e) => {
    setTriggerTypeMonthly(e.value);
  };

  const handleTriggerTypeChangeYearly = (e) => {
    setTriggerTypeYearly(e.value);
  };

  const handleSelectionChange = (e) => {
    setSelectedDays(e.value);
    if (popupScheduleVisible === false) {
      setSelectedDays([]);
    }
  };

  const handleCancel = () => {
    onClose();
    setTriggerType("");
  };

  useEffect(() => {
    const fetchTypesOfWork = async () => {
      axios
        .get<TypeOfWork[]>(
          `/api/companies/${user?.companyId || 0}/types-of-work`
        )
        .then((response) => {
          setTypesOfWork(
            response.data.map((element) => {
              if (element.id === 1) {
                return {
                  id: 1,
                  name: "Brak",
                };
              }

              return element;
            })
          );
        })
        .catch(() => {
          notify(t("errorOccurredWhileDataDownload"), "error", 5000);
        });
    };

    const fetchTypesOfDayOff = async () => {
      axios
        .get<TypeOfDayOff[]>(
          `/api/companies/${user?.companyId || 0}/types-of-day-off`
        )
        .then((response) => {
          setTypesOfDayOff(response.data);
        })
        .catch(() => {
          notify(t("errorOccurredWhileDataDownload"), "error", 5000);
        });
    };

    const fetchWorkPlan = async (
      companyId: number,
      employeeId: number,
      date: string
    ) => {
      axios
        .get<{
          typeOfDay: number;
          kindOfDayOff: number | null;
          exists: boolean;
          entrances: {
            from: string;
            to: string;
            typeOfWork: number;
            departmentId: number;
            projectId: number;
            note: string;
          }[];
        }>(
          `/api/companies/${companyId}/employees/${employeeId}/work-plan-for-day/${date}`
        )
        .then(({ data }) => {
          setTypeOfDay(data.typeOfDay);
          setKindOfDayOff(data.kindOfDayOff ?? null);
          setPlanExists(!!data.exists);
          setEntrances(
            data.entrances.map((entrance) => ({
              from: new Date(entrance.from),
              to: new Date(entrance.to),
              typeOfWork: entrance.typeOfWork,
              departmentId: entrance.departmentId,
              projectId: entrance.projectId,
              note: entrance.note,
              showDetails: false,
            }))
          );
        })
        .catch(() => {
          notify(t("errorOccurredWhileDataDownload"), "error", 5000);
        });
    };

    setLoading(true);
    fetchTypesOfWork();
    fetchTypesOfDayOff();
    setSelectedEmployees(
      employeesWithDates.map(
        ({ employee }) => `${employee.companyId},${employee.employeeId}`
      )
    );

    setDisableEmployeesTagBox(
      !(
        employeesWithDates.length === 1 &&
        employeesWithDates[0].dates.length === 1
      )
    );

    setHideDateInputs(
      !(
        employeesWithDates.length === 1 &&
        employeesWithDates[0].dates.length === 1
      )
    );

    setFrom(employeesWithDates[0].dates[0]);
    setTo(employeesWithDates[0].dates[0]);
    setLoading(false);

    if (
      employeesWithDates.length === 1 &&
      employeesWithDates[0].dates.length === 1
    ) {
      const { companyId, employeeId } = employeesWithDates[0].employee;
      const [date] = employeesWithDates[0].dates;

      fetchWorkPlan(companyId, employeeId, formatDate(date, "yyyy-MM-dd"));
    }

    setSelectedEmployees(
      employeesWithDates.map(
        (entry) => `${entry.employee.companyId},${entry.employee.employeeId}`
      )
    );
  }, [employeesWithDates, t, user?.companyId]);

  useEffect(() => {
    if (typeOfDay === 1) {
      setIsDayOffOrHoliday(false);
      setEntrances([
        {
          from: new Date(1900, 0, 1, 7, 0, 0, 0),
          to: new Date(1900, 0, 1, 15, 0, 0, 0),
          typeOfWork: 2,
          departmentId: 1,
          projectId: 1,
          note: "",
          showDetails: false,
        },
      ]);
    }

    if ([2, 3].includes(typeOfDay)) {
      setIsDayOffOrHoliday(true);
      setEntrances([
        {
          from: new Date(1900, 0, 1, 0, 0, 0, 0),
          to: new Date(1900, 0, 2, 0, 0, 0, 0),
          typeOfWork: 1,
          departmentId: 1,
          projectId: 1,
          note: "",
          showDetails: false,
        },
      ]);
    }
  }, [typeOfDay]);

  useEffect(() => {
    if (from && to) {
      const dates = eachDayOfInterval({
        start: from,
        end: to,
      });

      let weekendDays = 0;
      for (const date of dates) {
        if (date.getDay() === 6 || date.getDay() === 0) {
          weekendDays++;
        }
      }

      setOnlyWeekendDays(
        skipDaysOffAndHolidays && dates.length === weekendDays
      );
    }
  }, [from, to, skipDaysOffAndHolidays]);

  const sortedEmployees = useMemo(
    () =>
      [...employeesToSelect]
        .sort((a, b) =>
          a.name.localeCompare(b.name, "pl", { sensitivity: "base" }),
        )
        .map((employee) => ({
          id: `${employee.companyId},${employee.employeeId}`,
          name: employee.name,
        })),
    [employeesToSelect],
  );

  if (loading) {
    return <LoadPanel shading shadingColor="rgba(0, 0, 0, 0.3)" visible />;
  }

  const popupTitle = isSingleEmployeeSingleDate
    ? planExists
      ? t("workPlanEditor.headerEdit")
      : t("workPlanEditor.headerAdd")
    : t("workPlanEditor.header");

  const handleDelete = () => {
    if (!onDelete || !isSingleEmployeeSingleDate) return;
    confirm(t("workPlanEditor.deleteConfirm"), t("common.confirmation")).then(
      async (value) => {
        if (value !== true) return;
        try {
          await onDelete({
            companyId: employeesWithDates[0].employee.companyId,
            employeeId: employeesWithDates[0].employee.employeeId,
            date: employeesWithDates[0].dates[0],
          });
        } catch {
          notify(
            t("workPlanEditor.errorOccurredWhileWorkPlanDelete"),
            "error",
            5000,
          );
        }
      },
    );
  };

  return (
    <Popup
      visible
      title={popupTitle}
      onHiding={handleCancel}
      fullScreen={isMobile}
      maxWidth={isMobile ? undefined : 1000}
      height={isMobile ? "100%" : "auto"}
      maxHeight={isMobile ? undefined : "80vh"}
      showCloseButton
      dragEnabled={!isMobile}
    >
      {onlyWeekendDays ? (
        <div className="p-4 mb-3 rounded-md border-yellow-500 bg-yellow-50 text-yellow-800 text-sm text-justify">
          {t("workPlanEditor.onlyWeekendDaysAlert")}
        </div>
      ) : null}
      <div className="flex flex-col md:flex-row gap-6">
        <div className="basis-1/2">
          <div className="mb-2 font-bold text-base">
            {t("workPlanEditor.employeesHeader")}
          </div>
          <TagBox
            className="mb-4"
            dataSource={sortedEmployees}
            valueExpr="id"
            displayExpr="name"
            value={selectedEmployees}
            onValueChange={(value) => setSelectedEmployees(value)}
            searchEnabled
            hideSelectedItems
            disabled={disableEmployeesTagBox}
          />

          <div className="mb-2 font-bold text-base">
            {t("workPlanEditor.periodAndTypeOfDayHeader")}
          </div>
          {!hideDateInputs ? (
            <div
              className={clsx(
                "mb-6 flex gap-4",
                isMobile ? "flex-col" : "flex-row",
              )}
            >
              <div className={isMobile ? "" : "basis-1/2"}>
                <DateBox
                  type="date"
                  labelMode="static"
                  label={t("workPlanEditor.fromDate")}
                  value={from}
                  pickerType={isMobile ? "rollers" : "calendar"}
                  onValueChange={(value) => {
                    if (value instanceof Date) {
                      setFrom(value);
                    }
                  }}
                />
              </div>
              <div className={isMobile ? "" : "basis-1/2"}>
                <DateBox
                  type="date"
                  labelMode="static"
                  label={t("workPlanEditor.toDate")}
                  value={to}
                  pickerType={isMobile ? "rollers" : "calendar"}
                  onValueChange={(value) => {
                    if (value instanceof Date) {
                      setTo(value);
                    }
                  }}
                />
              </div>
            </div>
          ) : null}
          <SelectBox
            className="mb-4"
            dataSource={dayTypes}
            valueExpr="id"
            displayExpr="name"
            value={typeOfDay}
            onValueChange={(value) => {
              if (value) {
                setTypeOfDay(value);
              }
            }}
            labelMode="static"
            label={t("workPlanEditor.typeOfDay")}
          />
          {isDayOffOrHoliday ? (
            <SelectBox
              className="mb-4"
              dataSource={typesOfDayOff}
              valueExpr="id"
              displayExpr="name"
              value={kindOfDayOff}
              onValueChange={(value) => {
                if (value) {
                  setKindOfDayOff(value);
                }
              }}
              labelMode="static"
              label={t("workPlanEditor.kindOfDayOff")}
            />
          ) : null}
          <CheckBox
            className="ml-0"
            text={t("workPlanEditor.skipDaysOffAndHolidays")}
            value={skipDaysOffAndHolidays}
            onValueChange={(value) => {
              if (!value) {
                setSkipDaysOffAndHolidays(false);
              } else {
                setSkipDaysOffAndHolidays(value || false);
              }
            }}
          />
        </div>
        <div className="basis-1/2">
          <div
            className={clsx(
              "mb-3 flex items-center gap-2",
              isMobile ? "flex-col items-stretch" : "flex-row justify-between",
            )}
          >
            <div className="font-bold text-base">
              {t("workPlanEditor.dayTimetableHeader")}
            </div>
            {!isDayOffOrHoliday ? (
              <Button
                stylingMode="outlined"
                type="default"
                icon="plus"
                text={t("workPlanEditor.addPosition")}
                width={isMobile ? "100%" : undefined}
                onClick={() => {
                  const from = new Date(entrances[entrances.length - 1].to);
                  const to = addHours(from, 1);

                  setEntrances(() => [
                    ...entrances,
                    {
                      from,
                      to,
                      typeOfWork: 2,
                      departmentId: 1,
                      projectId: 1,
                      note: "",
                      showDetails: false,
                    },
                  ]);
                }}
              />
            ) : null}
          </div>
          <div className="flex flex-col gap-4">
            {entrances.map((entry, index) => (
              <div
                key={`entrance-${index}`}
                className={clsx(
                  "p-4",
                  "rounded",
                  theme === "light" ? "bg-gray-100" : "bg-[#383838]"
                )}
              >
                <div className="mb-5 flex flex-row gap-4">
                  <div className="basis-1/2">
                    <DateBox
                      type="time"
                      useMaskBehavior
                      pickerType={isMobile ? "rollers" : "list"}
                      interval={30}
                      labelMode="static"
                      label={t("workPlanEditor.fromHour")}
                      value={entrances[index].from}
                      valueChangeEvent="change keyup blur"
                      onValueChange={(value) => {
                        if (value instanceof Date) {
                          setEntrances(
                            produce(entrances, (draft) => {
                              draft[index].from = value;
                            })
                          );
                        }
                      }}
                    />
                  </div>
                  <div className="basis-1/2">
                    <DateBox
                      type="time"
                      useMaskBehavior
                      pickerType={isMobile ? "rollers" : "list"}
                      interval={30}
                      labelMode="static"
                      label={t("workPlanEditor.toHour")}
                      value={entrances[index].to}
                      valueChangeEvent="change keyup blur"
                      onValueChange={(value) => {
                        if (value instanceof Date) {
                          setEntrances(
                            produce(entrances, (draft) => {
                              draft[index].to = value;
                            })
                          );
                        }
                      }}
                    />
                  </div>
                </div>
                <SelectBox
                  label={t("workPlanEditor.kindOfWork")}
                  labelMode="static"
                  dataSource={typesOfWork}
                  valueExpr="id"
                  displayExpr="name"
                  value={entrances[index].typeOfWork}
                  onValueChange={(value) => {
                    setEntrances(
                      produce(entrances, (draft) => {
                        draft[index].typeOfWork = value;
                      })
                    );
                  }}
                />

                {entry.showDetails ? (
                  <>
                    {!isDayOffOrHoliday ? (
                      <div>
                        <div className="mb-5 mt-5">
                          {/* <Lookup
                            label={t("workPlanEditor.project")}
                            labelMode="static"
                            dataSource={projects}
                            valueExpr="id"
                            displayExpr="acronym"
                            value={entrances[index].projectId}
                            onValueChange={(value) => {
                              setEntrances(
                                produce(entrances, (draft) => {
                                  draft[index].projectId = value;
                                })
                              );
                            }}
                            dropDownOptions={{
                              showTitle: false,
                            }}
                          /> */}
                        </div>
                      </div>
                    ) : null}
                    <TextArea
                      labelMode="static"
                      label={t("workPlanEditor.note")}
                      value={entrances[index].note}
                      valueChangeEvent="keyup"
                      onValueChange={(value) =>
                        setEntrances(
                          produce(entrances, (draft) => {
                            draft[index].note = value;
                          })
                        )
                      }
                    />
                  </>
                ) : null}
                <div
                  className={clsx(
                    "mt-3 flex gap-2",
                    isMobile ? "flex-col" : "flex-row justify-between",
                  )}
                >
                  {entrances.length > 1 ? (
                    <Button
                      stylingMode="outlined"
                      type="danger"
                      icon="trash"
                      text={t("workPlanEditor.removePosition")}
                      width={isMobile ? "100%" : undefined}
                      onClick={() => {
                        confirm(
                          t(
                            "presencePopup.areYouSureYouWantToDeleteSelectedPosition",
                          ),
                          t("common.confirmation"),
                        ).then((value) => {
                          if (value === true) {
                            setEntrances(
                              produce(entrances, (draft) => {
                                draft.splice(index, 1);
                              }),
                            );
                          }
                        });
                      }}
                    />
                  ) : null}
                  <Button
                    stylingMode="outlined"
                    type="default"
                    icon={entry.showDetails ? "chevronup" : "chevrondown"}
                    text={
                      entry.showDetails
                        ? t("workPlanEditor.hideDetails")
                        : t("workPlanEditor.showDetails")
                    }
                    width={isMobile ? "100%" : undefined}
                    onClick={() => {
                      setEntrances(
                        produce(entrances, (draft) => {
                          draft[index].showDetails =
                            !entrances[index].showDetails;
                        }),
                      );
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="button-add-work mt-4">
        {popupScheduleVisible === true && (
          <div className="schedule-change">
            <div>
              <RadioGroup
                items={triggerOptions}
                value={triggerType}
                valueExpr="key"
                displayExpr="label"
                onValueChanged={handleTriggerTypeChange}
                layout="horizontal"
              />
            </div>

            {triggerType && (
              <div>
                {triggerType === "schedule.daily" && (
                  <div>
                    <RadioGroup
                      items={triggerOptionsDaily}
                      value={triggerTypeDaily}
                      valueExpr="key"
                      displayExpr="label"
                      onValueChanged={handleTriggerTypeChangeDaily}
                      layout="horizontal"
                    />
                    {triggerTypeDaily === "schedule.what" && (
                      <div className="inline-flex items-center space-x-2">
                        <NumberBoxWrapper
                          value={numericValue}
                          min={1}
                          width={100}
                          onCommit={(val) => setNumericValue(val)}
                        />
                        <span>{t("schedule.days")}</span>
                      </div>
                    )}
                  </div>
                )}

                {triggerType === "schedule.weekly" && (
                  <div>
                    <div className="flex flex-wrap items-center gap-y-2 space-x-2">
                      <span>{t("schedule.repeatWhat")}</span>
                      <NumberBoxWrapper
                        value={numericValue}
                        min={1}
                        width={100}
                        onCommit={(val) => setNumericValue5(val)}
                      />
                      <span>{t("schedule.weekNextWeek")}</span>
                    </div>

                    <TagBox
                      items={daysOfWeek}
                      value={selectedDays}
                      displayExpr="name"
                      valueExpr="id"
                      showSelectionControls={true}
                      applyValueMode="useButtons"
                      onValueChanged={handleSelectionChange}
                      placeholder={t("schedule.chooseWeekDay")}
                      className="mt-4"
                    />
                  </div>
                )}
                {triggerType === "schedule.monthly" && (
                  <div>
                    <RadioGroup
                      items={triggerOptionsMonthly}
                      value={triggerTypeMonthly}
                      valueExpr="key"
                      displayExpr="label"
                      onValueChanged={handleTriggerTypeChangeMonthly}
                      layout="horizontal"
                    />
                    <div className="flex flex-wrap items-center gap-y-2 space-x-2">
                      {triggerTypeMonthly === "schedule.monthDay" && (
                        <>
                          <NumberBox
                            min={1}
                            max={12}
                            value={numericValue1}
                            width={100}
                            showClearButton={true}
                            onValueChanged={handleNumberBoxChange(
                              setNumericValue1
                            )}
                            className="border border-gray-300 rounded p-1"
                          />
                          <span>{t("schedule.what")}</span>
                          <NumberBox
                            min={1}
                            value={numericValue2}
                            width={80}
                            showClearButton={true}
                            onValueChanged={handleNumberBoxChange(
                              setNumericValue2
                            )}
                            className="border border-gray-300 rounded p-1"
                          />
                          <span>{t("schedule.month")}</span>
                        </>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-y-2 space-x-2">
                      {triggerTypeMonthly === "schedule.inTheNextDay" && (
                        <>
                          <SelectBox
                            dataSource={daysOptionsInWeek}
                            valueExpr="value"
                            displayExpr="label"
                            value={selectedDayInWeek}
                            onValueChanged={(e) =>
                              setSelectedDayInWeek(e.value)
                            }
                            className="ml-2 border border-gray-300 rounded p-1 w-40"
                          />
                          <SelectBox
                            dataSource={daysOptions}
                            valueExpr="value"
                            displayExpr="label"
                            value={selectedDay}
                            onValueChanged={(e) => setSelectedDay(e.value)}
                            className="ml-2 border border-gray-300 rounded p-1 w-40"
                          />
                          <span>{t("schedule.monthWhat")}</span>
                          <NumberBox
                            min={1}
                            value={numericValue3}
                            width={100}
                            showClearButton={true}
                            onValueChanged={handleNumberBoxChange(
                              setNumericValue3
                            )}
                            className="border border-gray-300 rounded p-1"
                          />
                          <span>{t("schedule.month")}</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {triggerType === "schedule.yearly" && (
                  <div className="flex flex-col">
                    <div className="flex flex-wrap items-center gap-y-2 space-x-2">
                      <span>{t("schedule.repeatWhat")}</span>
                      <NumberBox
                        min={1}
                        value={numericValue4}
                        width={100}
                        showClearButton={true}
                        onValueChanged={handleNumberBoxChange(setNumericValue4)}
                        className="border border-gray-300 rounded p-1"
                      />
                      <span>{t("schedule.year")}</span>
                    </div>
                    <RadioGroup
                      items={triggerOptionsYearly}
                      value={triggerTypeYearly}
                      valueExpr="key"
                      displayExpr="label"
                      onValueChanged={handleTriggerTypeChangeYearly}
                      layout="horizontal"
                      className="mt-2"
                    />
                    <div className="flex flex-wrap items-center gap-y-2 space-x-2">
                      {triggerTypeYearly === "schedule.data" && (
                        <>
                          <NumberBox
                            min={1}
                            max={31}
                            value={numericValue5}
                            width={100}
                            showClearButton={true}
                            onValueChanged={handleNumberBoxChange(
                              setNumericValue5
                            )}
                          />
                          <SelectBox
                            dataSource={monthOptions}
                            valueExpr="value"
                            displayExpr="label"
                            value={monthSelect}
                            onValueChanged={(e) => setMonthSelect(e.value)}
                            className="ml-2 border border-gray-300 rounded p-1 w-40"
                          />
                          <span>{t("schedule.month")}</span>
                        </>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-y-2 space-x-2">
                      {triggerTypeYearly === "schedule.day" && (
                        <>
                          <SelectBox
                            dataSource={daysOptionsInWeek}
                            valueExpr="value"
                            displayExpr="label"
                            value={selectedDayInWeek}
                            onValueChanged={(e) =>
                              setSelectedDayInWeek(e.value)
                            }
                            className="ml-2 border border-gray-300 rounded p-1 w-40"
                          />
                          <SelectBox
                            dataSource={daysOptions}
                            valueExpr="value"
                            displayExpr="label"
                            value={selectedDay}
                            onValueChanged={(e) => setSelectedDay(e.value)}
                            className="ml-2 border border-gray-300 rounded p-1 w-40"
                          />
                          <span>{t("schedule.form")}</span>
                          <SelectBox
                            dataSource={monthOptions}
                            valueExpr="value"
                            displayExpr="label"
                            value={monthSelect}
                            onValueChanged={(e) => setMonthSelect(e.value)}
                            className="ml-2 border border-gray-300 rounded p-1 w-40"
                          />
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <Button
          className="button-add-work-w"
          text={t("More options")}
          type="default"
          onClick={handleMoreOptions}
          width={150}
        />
      </div>

      {onDelete && isSingleEmployeeSingleDate && planExists ? (
        <ToolbarItem
          toolbar="bottom"
          widget="dxButton"
          location="before"
          options={{
            stylingMode: "outlined",
            type: "danger",
            icon: "trash",
            text: t("workPlanEditor.delete"),
            onClick: handleDelete,
          }}
        />
      ) : null}

      <ToolbarItem
        toolbar="bottom"
        widget="dxButton"
        location="after"
        options={{
          stylingMode: "contained",
          type: "default",
          text: t("save"),
          disabled: onlyWeekendDays,
          onClick: () => {
            if (selectedEmployees.length === 0) {
              notify(
                t("workPlanEditor.validation.emptyEmployees"),
                "error",
                5000
              );
              return;
            }

            if (compareDesc(from, to) === -1) {
              notify(
                t("workPlanEditor.validation.invalidDateRange"),
                "error",
                5000
              );
              return;
            }

            axios
              .put(
                "/api/work-plan",
                selectedEmployees.map((employee) => {
                  const [companyId, employeeId] = employee.split(",");
                  let selectedDates: Date[] = [];
                  let triggeredByRange = false;

                  if (from && to) {
                    if (triggerType !== "") {
                      selectedDates = getDates(from, to);
                      triggeredByRange = true;
                    } else {
                      selectedDates = eachDayOfInterval({
                        start: from,
                        end: to,
                      });
                    }
                    setTriggerType("");
                  }

                  if (employeesWithDates.length > 0 && !triggeredByRange) {
                    employeesWithDates.forEach((emp) => {
                      selectedDates.push(...emp.dates);
                    });
                  }

                  const uniqueDates = Array.from(
                    new Set(
                      selectedDates.map((date) => format(date, "yyyy-MM-dd"))
                    )
                  );
                  selectedDates = uniqueDates.map((date) => new Date(date));

                  if (skipDaysOffAndHolidays) {
                    selectedDates = selectedDates.filter(
                      (date) => !isHolidayOrDayOff(date)
                    );
                  }

                  return {
                    employee: {
                      companyId: Number(companyId),
                      employeeId: Number(employeeId),
                    },
                    dates: selectedDates.map((date) =>
                      format(date, "yyyy-MM-dd")
                    ),
                    skipDaysOffAndHolidays,
                    typeOfDay,
                    kindOfDayOff,
                    entrances: entrances.map((entrance) => ({
                      from: format(entrance.from, "HH:mm:ss"),
                      to: format(entrance.to, "HH:mm:ss"),
                      typeOfWork: entrance.typeOfWork,
                      departmentId: entrance.departmentId,
                      projectId: entrance.projectId,
                      note: entrance.note,
                    })),
                  };
                })
              )
              .then(onUpdate)
              .catch(() => {
                notify(
                  t("workPlanEditor.errorOccurredWhiteWorkPlanSave"),
                  "error"
                );
              });
          },
        }}
      />
    </Popup>
  );
}
